import { useTranslation } from "react-i18next";
import type { FeatureFlagChange, FeatureFlagValueType } from "./types";
import { formatValueBlock } from "./format-value";

interface FeatureFlagHistoryTableProps {
  readonly items: readonly FeatureFlagChange[];
  readonly valueType: FeatureFlagValueType;
}

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function FeatureFlagHistoryTable({ items, valueType }: FeatureFlagHistoryTableProps) {
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <p
        data-testid="feature-flag-history-empty"
        className="font-sans text-sm font-normal text-muted-foreground"
      >
        {t("featureFlags.history.empty")}
      </p>
    );
  }

  return (
    <ol data-testid="feature-flag-history-list" className="space-y-3">
      {items.map((change) => (
        <li
          key={change.id}
          data-testid={`feature-flag-history-entry-${change.id}`}
          className="rounded-md border border-border bg-card p-3"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-sans text-xs font-medium text-muted-foreground">
              {formatDateTime(change.changedAt)}
            </span>
            <span className="font-sans text-xs font-normal text-muted-foreground">
              {change.changedBy ? (
                <>
                  {t("featureFlags.history.changedBy")}:{" "}
                  <span className="font-mono text-foreground">{change.changedBy}</span>
                </>
              ) : (
                t("featureFlags.history.systemSeed")
              )}
            </span>
          </div>

          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
              <p className="font-sans text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("featureFlags.history.previousValue")}
              </p>
              <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded border border-border bg-muted/30 p-2 font-mono text-[11px] text-foreground">
                {change.previousValue === null
                  ? t("featureFlags.history.noPreviousValue")
                  : formatValueBlock(change.previousValue, valueType)}
              </pre>
            </div>
            <div>
              <p className="font-sans text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("featureFlags.history.newValue")}
              </p>
              <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded border border-border bg-muted/30 p-2 font-mono text-[11px] text-foreground">
                {formatValueBlock(change.newValue, valueType)}
              </pre>
            </div>
          </div>

          {change.reason && (
            <p className="mt-2 font-sans text-sm font-normal text-foreground">
              <span className="font-medium text-muted-foreground">
                {t("featureFlags.history.reason")}:
              </span>{" "}
              {change.reason}
            </p>
          )}
        </li>
      ))}
    </ol>
  );
}
